import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, X, Phone, FileText, Video, RefreshCw, ArrowRightLeft,
  LogOut, ChevronRight, CheckCircle2, Heart, User, Stethoscope,
  Brain, TrendingUp, TrendingDown, Pen, ChevronDown, Sparkles,
  Activity, Smile, Frown, Meh,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { patients as rawPatients } from '@/data/mockData';

/* ═══ HELPERS ═══ */
const CM_NAMES = ['Dott.ssa L. Conti', 'Dott. M. Ferrara', 'Dott.ssa A. Romano', 'Dott. G. Neri'];
const EROGATORI = ['Coop. Salute Roma', 'Consorzio ADI Lazio', 'Fondazione Gemelli@Home', 'ASL Roma 2'];

function getPatient(id) {
  const p = rawPatients.find(x => x.id === Number(id));
  if (!p) return null;
  return { ...p, caseManager: CM_NAMES[p.id % 4], erogatore: EROGATORI[p.id % 4], paiVersion: 'v1.0', paiStatus: 'In Scadenza' };
}

/* ═══ MOCK DATA ═══ */
const VITALS_DATA = [
  { giorno: '01/03', pas: 138, fc: 78, spo2: 96 },
  { giorno: '08/03', pas: 142, fc: 82, spo2: 95 },
  { giorno: '15/03', pas: 135, fc: 76, spo2: 97 },
  { giorno: '22/03', pas: 148, fc: 88, spo2: 94 },
  { giorno: '29/03', pas: 140, fc: 80, spo2: 96 },
  { giorno: '05/04', pas: 132, fc: 74, spo2: 97 },
];

const PROMS = [
  { label: 'EQ-5D (Qualità della vita)', score: 62, max: 100, prev: 48, color: 'bg-emerald-500' },
  { label: 'Barthel Index (Autonomia)', score: 60, max: 100, prev: 45, color: 'bg-cyan-500' },
  { label: 'NRS Dolore (inverso)', score: 30, max: 100, prev: 70, color: 'bg-amber-500' },
];

const PREMS = [
  { label: 'Soddisfazione assistenza', icon: Smile, value: '4.2/5', sentiment: 'positive' },
  { label: 'Comunicazione équipe', icon: Meh, value: '3.5/5', sentiment: 'neutral' },
  { label: 'Tempestività interventi', icon: Smile, value: '4.0/5', sentiment: 'positive' },
];

const SETTING_OPTIONS = ['RSA', 'Hospice', 'Ospedale di Comunità', 'Riabilitazione Intensiva', 'ADI di altro distretto'];

/* ═══ MAIN ═══ */
export default function RivalutazioneUvmdDecisionRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const patient = getPatient(id);

  const [selectedOption, setSelectedOption] = useState(null);
  const [nuovaComplessita, setNuovaComplessita] = useState('');
  const [nuovaScadenza, setNuovaScadenza] = useState('');
  const [destinazione, setDestinazione] = useState('');
  const [relazioneDimissione, setRelazioneDimissione] = useState('');
  const [firmaValidata, setFirmaValidata] = useState(false);
  const [showSchedaPaziente, setShowSchedaPaziente] = useState(false);

  if (!patient) {
    return (
      <AppLayout role="medico">
        <div className="flex items-center justify-center h-96"><p className="text-slate-500">Paziente non trovato.</p></div>
      </AppLayout>
    );
  }

  const canConfirm = firmaValidata && selectedOption && (
    (selectedOption === 'rinnovo' && nuovaComplessita) ||
    (selectedOption === 'cambio' && destinazione) ||
    (selectedOption === 'dimissione' && relazioneDimissione.trim().length > 10)
  );

  const toggleOption = (opt) => setSelectedOption(prev => prev === opt ? null : opt);

  return (
    <AppLayout role="medico">
      <div className="flex flex-col h-full" data-testid="rivalutazione-page">

        {/* ══ TOPBAR ══ */}
        <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-5 py-3.5 flex items-center justify-between shrink-0" data-testid="topbar">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/medico/pazienti/${id}`)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-sm font-bold text-slate-800 dark:text-white leading-tight">Rivalutazione PAI</h1>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">{patient.cognome} {patient.nome} — {patient.diagnosiPrincipale?.split('(')[0]?.trim()}</p>
            </div>
          </div>
          <button onClick={() => navigate(`/medico/pazienti/${id}`)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ══ SCROLLABLE CONTENT ══ */}
        <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-[#0f172a]">
          <div className="max-w-[1600px] mx-auto p-6">

            {/* ══ ISOLA 1: HEADER PAZIENTE ══ */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6 mb-6" data-testid="patient-header-island">
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-lg font-bold text-slate-800 dark:text-white">{patient.cognome} {patient.nome}</h2>
                    <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-0 text-[10px] font-bold px-2 py-0.5">
                      PAI {patient.paiVersion} · {patient.paiStatus}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">{patient.eta} anni · {patient.sesso} · CF: {patient.codiceFiscale}</p>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
                    <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                      <Heart className="w-3.5 h-3.5 text-red-400" />
                      <span className="font-medium">{patient.diagnosiPrincipale?.split('(')[0]?.trim()}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                      <User className="w-3.5 h-3.5" />
                      <span>Caregiver: {patient.caregiver?.nome || 'N/D'} ({patient.caregiver?.relazione})</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                      <Stethoscope className="w-3.5 h-3.5" />
                      <span>CM: {patient.caseManager}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                      <Activity className="w-3.5 h-3.5" />
                      <span>Erogatore: {patient.erogatore}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button className="flex items-center gap-2 px-4 py-2 border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 text-xs font-medium rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/20 transition-colors" data-testid="cta-chiama">
                    <Phone className="w-3.5 h-3.5" /> Chiama Paziente
                  </button>
                  <button onClick={() => setShowSchedaPaziente(true)} className="flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-xs font-medium rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" data-testid="cta-scheda">
                    <FileText className="w-3.5 h-3.5" /> Scheda paziente
                  </button>
                </div>
              </div>
            </div>

            {/* ══ GRID DUE COLONNE ══ */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

              {/* ════ COLONNA SINISTRA ════ */}
              <div data-testid="colonna-sinistra">
                <h3 className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">Quadro Clinico e Dati di Esito</h3>

                {/* Isola: Andamento Telemonitoraggio */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6 mb-4" data-testid="island-telemonitoraggio">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-cyan-500" />
                      <h4 className="text-sm font-bold text-slate-800 dark:text-white">Andamento Telemonitoraggio</h4>
                    </div>
                    <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-0 text-[10px] font-semibold">Ultimi 45 giorni</Badge>
                  </div>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={VITALS_DATA} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border, #e2e8f0)" />
                        <XAxis dataKey="giorno" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                        <Line type="monotone" dataKey="pas" name="PA Sistolica" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="fc" name="Freq. Cardiaca" stroke="#06b6d4" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="spo2" name="SpO2" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex items-center gap-5 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                    {[
                      { label: 'PA Sistolica', color: 'bg-red-500', val: '132 mmHg' },
                      { label: 'Freq. Cardiaca', color: 'bg-cyan-500', val: '74 bpm' },
                      { label: 'SpO2', color: 'bg-emerald-500', val: '97%' },
                    ].map(v => (
                      <div key={v.label} className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${v.color}`} />
                        <span className="text-[10px] text-slate-500 dark:text-slate-400">{v.label}: <strong className="text-slate-700 dark:text-slate-200">{v.val}</strong></span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Isola: Esiti PROMs e PREMs */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6" data-testid="island-proms">
                  <div className="flex items-center gap-2 mb-5">
                    <TrendingUp className="w-4 h-4 text-violet-500" />
                    <h4 className="text-sm font-bold text-slate-800 dark:text-white">Esiti PROMs e PREMs</h4>
                  </div>
                  {/* PROMs */}
                  <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">PROMs (Patient-Reported Outcome Measures)</p>
                  <div className="flex flex-col gap-4 mb-6">
                    {PROMS.map((p, i) => {
                      const improved = p.score > p.prev;
                      return (
                        <div key={i} data-testid={`prom-${i}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs font-medium text-slate-700 dark:text-slate-200">{p.label}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-slate-400 line-through">{p.prev}</span>
                              {improved
                                ? <TrendingUp className="w-3 h-3 text-emerald-500" />
                                : <TrendingDown className="w-3 h-3 text-red-500" />}
                              <span className="text-xs font-bold text-slate-800 dark:text-white">{p.score}/{p.max}</span>
                            </div>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div className={`h-full ${p.color} rounded-full transition-all`} style={{ width: `${p.score}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* PREMs */}
                  <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">PREMs (Patient-Reported Experience Measures)</p>
                  <div className="flex flex-col gap-2">
                    {PREMS.map((p, i) => {
                      const Icon = p.icon;
                      const sentimentColor = p.sentiment === 'positive' ? 'text-emerald-500' : p.sentiment === 'negative' ? 'text-red-500' : 'text-amber-500';
                      return (
                        <div key={i} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/50" data-testid={`prem-${i}`}>
                          <div className="flex items-center gap-2.5">
                            <Icon className={`w-4 h-4 ${sentimentColor}`} />
                            <span className="text-xs text-slate-700 dark:text-slate-200">{p.label}</span>
                          </div>
                          <span className="text-xs font-bold text-slate-800 dark:text-white">{p.value}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* ════ COLONNA DESTRA ════ */}
              <div data-testid="colonna-destra">
                <h3 className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">Supporto Decisionale e Azioni</h3>

                {/* Isola AI (Viola Gradient) */}
                <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-6 text-white mb-4 shadow-lg shadow-violet-600/15" data-testid="island-ai">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-5 h-5 text-violet-200" />
                    <h4 className="text-sm font-bold">Suggerimento AI — Stratificazione Predittiva</h4>
                  </div>
                  <p className="text-sm text-violet-100 leading-relaxed mb-4">
                    Sulla base dei dati di telemonitoraggio e degli esiti PROMs, si consiglia il <strong>rinnovo del PAI con riduzione della complessità a "Media"</strong>. Il Barthel Index è migliorato (+15 punti), ma il dolore cronico (NRS 7/10) richiede rivalutazione antalgica. Probabilità di ospedalizzazione nei prossimi 90gg: <strong>12% (bassa)</strong>.
                  </p>
                  <div className="flex items-center gap-3">
                    <button className="px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white text-xs font-semibold rounded-lg transition-colors border border-white/20" data-testid="ai-accetta-btn">
                      <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Accetta Suggerimento</span>
                    </button>
                    <button className="px-4 py-2 text-violet-200 hover:text-white text-xs font-medium rounded-lg transition-colors" data-testid="ai-dettagli-btn">
                      Vedi dettagli analisi
                    </button>
                  </div>
                </div>

                {/* Isola UVMD */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-4 mb-8 flex items-center justify-between" data-testid="island-uvmd">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
                      <Video className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-800 dark:text-white">Gestione UVMD</p>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400">Discuti il caso con l'équipe in tempo reale</p>
                    </div>
                  </div>
                  <button className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-xs font-medium rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" data-testid="uvmd-teleconsulto-btn">
                    Avvia Teleconsulto UVMD
                  </button>
                </div>

                {/* Titolo Sezione 2 */}
                <h3 className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">Esito Finale — Decisione</h3>

                {/* Opzione A: Rinnovo */}
                <div className="mb-3" data-testid="decision-rinnovo">
                  <button
                    onClick={() => toggleOption('rinnovo')}
                    className={`w-full bg-white dark:bg-slate-900 rounded-xl border p-4 flex items-center justify-between transition-all
                      ${selectedOption === 'rinnovo'
                        ? 'border-emerald-400 dark:border-emerald-600 ring-1 ring-emerald-200 dark:ring-emerald-800/40 shadow-md'
                        : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'}`}
                    data-testid="option-rinnovo"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${selectedOption === 'rinnovo' ? 'bg-emerald-100 dark:bg-emerald-900/40' : 'bg-slate-100 dark:bg-slate-800'}`}>
                        <RefreshCw className={`w-4 h-4 ${selectedOption === 'rinnovo' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`} />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-semibold text-slate-800 dark:text-white">Rinnovo / Modifica PAI</p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">Proroga il piano o modifica l'intensità</p>
                      </div>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${selectedOption === 'rinnovo' ? 'rotate-90' : ''}`} />
                  </button>
                  {selectedOption === 'rinnovo' && (
                    <div className="bg-white dark:bg-slate-900 rounded-b-xl border border-t-0 border-slate-200 dark:border-slate-800 px-5 py-4 -mt-1 ml-2 mr-2 border-l-2 border-l-emerald-400" data-testid="rinnovo-fields">
                      <div className="flex flex-col gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">Nuova Complessità</label>
                          <div className="flex gap-2" data-testid="complessita-selector">
                            {['Bassa', 'Media', 'Alta'].map(c => (
                              <button key={c} onClick={() => setNuovaComplessita(c)} className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-all ${nuovaComplessita === c ? (c === 'Bassa' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 ring-2 ring-emerald-300 dark:ring-emerald-700' : c === 'Media' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 ring-2 ring-amber-300 dark:ring-amber-700' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 ring-2 ring-red-300 dark:ring-red-700') : 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`} data-testid={`complessita-${c.toLowerCase()}`}>
                                {c}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Nuova Scadenza</label>
                          <input type="date" value={nuovaScadenza} onChange={e => setNuovaScadenza(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30" data-testid="nuova-scadenza-input" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Opzione B: Cambio Setting */}
                <div className="mb-3" data-testid="decision-cambio">
                  <button
                    onClick={() => toggleOption('cambio')}
                    className={`w-full bg-white dark:bg-slate-900 rounded-xl border p-4 flex items-center justify-between transition-all
                      ${selectedOption === 'cambio'
                        ? 'border-amber-400 dark:border-amber-600 ring-1 ring-amber-200 dark:ring-amber-800/40 shadow-md'
                        : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'}`}
                    data-testid="option-cambio"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${selectedOption === 'cambio' ? 'bg-amber-100 dark:bg-amber-900/40' : 'bg-slate-100 dark:bg-slate-800'}`}>
                        <ArrowRightLeft className={`w-4 h-4 ${selectedOption === 'cambio' ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400'}`} />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-semibold text-slate-800 dark:text-white">Cambio Setting Assistenziale</p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">Trasferimento verso RSA, Hospice, OdC</p>
                      </div>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${selectedOption === 'cambio' ? 'rotate-90' : ''}`} />
                  </button>
                  {selectedOption === 'cambio' && (
                    <div className="bg-white dark:bg-slate-900 rounded-b-xl border border-t-0 border-slate-200 dark:border-slate-800 px-5 py-4 -mt-1 ml-2 mr-2 border-l-2 border-l-amber-400" data-testid="cambio-fields">
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Destinazione</label>
                      <div className="relative">
                        <select value={destinazione} onChange={e => setDestinazione(e.target.value)} className="w-full h-10 px-3 pr-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500/30 appearance-none" data-testid="destinazione-select">
                          <option value="">Seleziona destinazione...</option>
                          {SETTING_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Opzione C: Chiusura */}
                <div className="mb-6" data-testid="decision-dimissione">
                  <button
                    onClick={() => toggleOption('dimissione')}
                    className={`w-full bg-white dark:bg-slate-900 rounded-xl border p-4 flex items-center justify-between transition-all
                      ${selectedOption === 'dimissione'
                        ? 'border-red-400 dark:border-red-600 ring-1 ring-red-200 dark:ring-red-800/40 shadow-md'
                        : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'}`}
                    data-testid="option-dimissione"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${selectedOption === 'dimissione' ? 'bg-red-100 dark:bg-red-900/40' : 'bg-slate-100 dark:bg-slate-800'}`}>
                        <LogOut className={`w-4 h-4 ${selectedOption === 'dimissione' ? 'text-red-600 dark:text-red-400' : 'text-slate-400'}`} />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-semibold text-slate-800 dark:text-white">Dimissione / Chiusura PAI</p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">Fine percorso o raggiungimento obiettivi</p>
                      </div>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${selectedOption === 'dimissione' ? 'rotate-90' : ''}`} />
                  </button>
                  {selectedOption === 'dimissione' && (
                    <div className="bg-white dark:bg-slate-900 rounded-b-xl border border-t-0 border-slate-200 dark:border-slate-800 px-5 py-4 -mt-1 ml-2 mr-2 border-l-2 border-l-red-400" data-testid="dimissione-fields">
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Relazione di Dimissione <span className="font-normal text-slate-400">(obbligatoria)</span></label>
                      <textarea value={relazioneDimissione} onChange={e => setRelazioneDimissione(e.target.value)} rows={4} placeholder="Motivazioni cliniche, obiettivi raggiunti e raccomandazioni post-dimissione..." className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/30 resize-none" data-testid="relazione-dimissione-textarea" />
                    </div>
                  )}
                </div>

                {/* ── FIRMA ── */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-5" data-testid="firma-section">
                  <label className="flex items-start gap-3 cursor-pointer mb-4" data-testid="firma-checkbox-label">
                    <input type="checkbox" checked={firmaValidata} onChange={e => setFirmaValidata(e.target.checked)} className="w-4 h-4 mt-0.5 rounded border-slate-300 dark:border-slate-600 text-teal-600 focus:ring-teal-500/30" data-testid="firma-checkbox" />
                    <span className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">Dichiaro di aver validato i dati clinici con l'équipe multidimensionale e di procedere alla firma dell'esito di rivalutazione.</span>
                  </label>
                  <button
                    disabled={!canConfirm}
                    className={`w-full py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2
                      ${canConfirm
                        ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-lg shadow-teal-600/20'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed'}`}
                    data-testid="conferma-firma-btn"
                  >
                    <Pen className="w-4 h-4" /> Conferma e Firma Digitalmente
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══ MODALE SCHEDA PAZIENTE ══ */}
      {showSchedaPaziente && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowSchedaPaziente(false)} />
          <div className="relative w-full max-w-lg mx-4 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="modal-scheda-paziente">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
              <h2 className="text-sm font-bold text-slate-800 dark:text-white">Scheda Anagrafica Paziente</h2>
              <button onClick={() => setShowSchedaPaziente(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="modal-scheda-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 flex flex-col gap-3 text-sm">
              {[
                ['Nome', `${patient.cognome} ${patient.nome}`],
                ['Codice Fiscale', patient.codiceFiscale],
                ['Età / Sesso', `${patient.eta} anni · ${patient.sesso}`],
                ['Diagnosi Principale', patient.diagnosiPrincipale],
                ['Caregiver', `${patient.caregiver?.nome || 'N/D'} (${patient.caregiver?.relazione})`],
                ['Telefono', patient.caregiver?.telefono || patient.telefono],
                ['Indirizzo', patient.indirizzo || 'Non disponibile'],
                ['Erogatore', patient.erogatore],
                ['Case Manager', patient.caseManager],
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between items-start">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400 w-36 shrink-0">{l}</span>
                  <span className="text-xs text-slate-800 dark:text-white text-right">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
